import React, { useState, useEffect, useRef } from 'react';
import './JayrajChatbot.css';
import api from '../Api';

const JayrajChatbot = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [typingMessageId, setTypingMessageId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0); // Tracks unread notification count

  const messagesEndRef = useRef(null);
  const token = localStorage.getItem('accessToken');

  const [sessionUuid] = useState(() => {
    let savedUuid = localStorage.getItem('jayraj_chat_session');
    if (!savedUuid) {
      savedUuid = crypto.randomUUID 
        ? crypto.randomUUID() 
        : 'xxxx-xxxx-4xxx-yxxx-xxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
          });
      localStorage.setItem('jayraj_chat_session', savedUuid);
    }
    return savedUuid;
  });

  const quickActions = [
    { label: '🏨 Book a Hotel', action: 'I want to book a hotel' },
    { label: '🗺️ Explore Tours', action: 'Tell me about tour packages' },
    { label: '💍 Destination Wedding', action: 'I am looking for a destination wedding venue' },
    { label: '🎧 Chat with Agent', action: 'Connect me with a live agent' }
  ];

  const quickChips = [
    { label: '✈️ Find Tours', text: 'Find available tours' },
    { label: '🏨 Book a Hotel', text: 'Book a hotel room' },
    { label: '💍 Wedding Enquiry', text: 'Destination wedding enquiry' }
  ];

  // Native Web Audio API chime sound
  const playNotificationSound = () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      
      const ctx = new AudioContext();
      
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(587.33, ctx.currentTime);
      gain1.gain.setValueAtTime(0.1, ctx.currentTime);
      gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(ctx.currentTime);
      osc1.stop(ctx.currentTime + 0.3);

      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(880, ctx.currentTime + 0.1);
      gain2.gain.setValueAtTime(0.1, ctx.currentTime + 0.1);
      gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(ctx.currentTime + 0.1);
      osc2.stop(ctx.currentTime + 0.4);
    } catch (e) {
      console.warn('Audio playback prevented until interaction', e);
    }
  };

  // Play sound & clear unread badge if chat is open on mount
  useEffect(() => {
    playNotificationSound();
  }, []);

  // Clear unread count when chat window is opened
  const handleOpenChat = () => {
    setIsOpen(true);
    setUnreadCount(0);
  };

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // Word-by-Word Stream Typing Effect
  const streamTypingEffect = (botMsgId, fullText, speed = 25) => {
    const words = fullText.split(' ');
    let currentWordIndex = 0;
    setTypingMessageId(botMsgId);

    const interval = setInterval(() => {
      currentWordIndex++;
      const currentText = words.slice(0, currentWordIndex).join(' ');

      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === botMsgId ? { ...msg, text: currentText } : msg
        )
      );

      if (currentWordIndex >= words.length) {
        clearInterval(interval);
        setTypingMessageId(null);
      }
    }, speed);
  };

  useEffect(() => {
    const fetchChatHistory = async () => {
      try {
        const response = await api.get(`/chatbot/history/${sessionUuid}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` })
          },
          credentials: 'include'
        });

        const resData = response.data || (await response.json?.()) || response;

        if (resData?.status && Array.isArray(resData.data) && resData.data.length > 0) {
          const allComments = resData.data.flatMap((item) => item.comments || [item]);

          const formattedMessages = allComments.map((comment, index) => ({
            id: comment.id || index + 1,
            sender: (comment.type === 'User' || comment.sender === 'user') ? 'user' : 'bot',
            text: comment.comment || comment.message || '',
            escalated: comment.escalated_to_care || comment.escalated || false,
            customer_care: comment.customer_care || null
          }));

          setMessages(formattedMessages);
        } else {
          setMessages([
            {
              id: 'welcome-1',
              sender: 'bot',
              text: 'Hello! I am your AI Assistant. How can we help you plan your stay, tour, or dream wedding today?'
            }
          ]);
        }
      } catch (error) {
        console.error('Error fetching chat history:', error);
        setMessages([
          {
            id: 'welcome-default',
            sender: 'bot',
            text: 'Hello! Welcome to Jayraj Group. How can we help you plan your stay, tour, or wedding today?'
          }
        ]);
      }
    };

    if (isOpen) {
      fetchChatHistory();
    }
  }, [isOpen, sessionUuid, token]);

  const handleSendMessage = async (textToSend) => {
    const text = textToSend || inputValue;
    if (!text.trim() || isLoading || typingMessageId) return;

    const userMessage = { id: Date.now(), sender: 'user', text };
    setMessages((prev) => [...prev, userMessage]);

    if (!textToSend) setInputValue('');
    setIsLoading(true);

    try {
      const response = await api.post('/chatbot/ask', {
        message: text,
        session_uuid: sessionUuid
      }, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      const data = response.data || (await response.json?.()) || response;
      const botReplyText = data.reply || data.message || data.response || 'Thank you for contacting Jayraj Group!';
      
      const newBotMsgId = Date.now() + 1;

      const botMessage = {
        id: newBotMsgId,
        sender: 'bot',
        text: '',
        escalated: data.escalated || data.escalated_to_care || false,
        customer_care: data.customer_care || null
      };

      setIsLoading(false);
      setMessages((prev) => [...prev, botMessage]);
      playNotificationSound();

      // Increments unread count only if chatbot is collapsed
      if (!isOpen) {
        setUnreadCount((prev) => prev + 1);
      }

      streamTypingEffect(newBotMsgId, botReplyText);

    } catch (error) {
      console.error('Error sending message:', error);
      setIsLoading(false);
      const errorMessage = {
        id: Date.now() + 1,
        sender: 'bot',
        text: 'Sorry, we are unable to process your request at the moment. Please try again later.'
      };
      setMessages((prev) => [...prev, errorMessage]);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleSendMessage();
  };

  return (
    <div className="jayraj-chatbot-wrapper">
      {!isOpen && (
        <button 
          className="chatbot-toggle-btn" 
          onClick={handleOpenChat}
          aria-label="Open Chat"
        >
          {unreadCount > 0 && <div className="btn-badge">{unreadCount}</div>}
          <span className="btn-icon">🤖</span>
        </button>
      )}

      {isOpen && (
        <div className="chatbot-container">
          <div className="chatbot-header">
            <div className="header-left">
              <span className="robot-icon-plain">🤖</span>
              <div className="brand-info">
                <h3>Jayraj Assistant</h3>
                <span className="status">
                  <span className="status-dot"></span> AI Bot Active
                </span>
              </div>
            </div>
            <button 
              className="close-btn" 
              onClick={() => setIsOpen(false)}
              aria-label="Close Chat"
            >
              Close ✖
            </button>
          </div>

          <div className="chatbot-body">
            <div className="quick-actions-grid">
              {quickActions.map((item, index) => (
                <button
                  key={index}
                  className="quick-action-card"
                  disabled={isLoading || !!typingMessageId}
                  onClick={() => handleSendMessage(item.action)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="messages-stream">
              {messages.map((msg) => (
                <div 
                  key={msg.id} 
                  className={`message-bubble ${msg.sender === 'user' ? 'user-msg' : 'bot-msg'}`}
                >
                  {msg.sender === 'bot' && (
                    <span className="bot-label">🤖 Jayraj AI</span>
                  )}
                  <p>
                    {msg.text}
                    {typingMessageId === msg.id && <span className="chatgpt-cursor" />}
                  </p>

                  {msg.escalated && msg.customer_care && typingMessageId !== msg.id && (
                    <div className="customer-care-actions" style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {msg.customer_care.tel_link && msg.customer_care.tel_link !== "tel:+" && (
                        <a href={msg.customer_care.tel_link} className="contact-link-btn" style={{ padding: '4px 8px', backgroundColor: '#007bff', color: '#fff', borderRadius: '4px', textDecoration: 'none', fontSize: '0.85rem' }}>
                          📞 Call Us
                        </a>
                      )}
                      {msg.customer_care.whatsapp_link && msg.customer_care.whatsapp_link !== "https://wa.me/" && (
                        <a href={msg.customer_care.whatsapp_link} target="_blank" rel="noopener noreferrer" className="contact-link-btn" style={{ padding: '4px 8px', backgroundColor: '#25D366', color: '#fff', borderRadius: '4px', textDecoration: 'none', fontSize: '0.85rem' }}>
                          💬 WhatsApp
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {isLoading && (
                <div className="message-bubble bot-msg typing-bubble">
                  <span className="bot-label">🤖 Jayraj AI</span>
                  <div className="typing-dots">
                    <span>.</span><span>.</span><span>.</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          <div className="quick-chips">
            {quickChips.map((chip, index) => (
              <button 
                key={index} 
                className="chip-btn"
                disabled={isLoading || !!typingMessageId}
                onClick={() => handleSendMessage(chip.text)}
              >
                {chip.label}
              </button>
            ))}
          </div>

          <div className="chatbot-footer">
            <div className="input-group">
              <input
                type="text"
                placeholder="Type your message here..."
                value={inputValue}
                disabled={isLoading || !!typingMessageId}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyPress}
              />
              <button 
                className="send-btn" 
                disabled={isLoading || !!typingMessageId}
                onClick={() => handleSendMessage()}
                aria-label="Send Message"
              >
                ➔
              </button>
            </div>
            <div className="footer-status">
              <span>24/7 Support</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JayrajChatbot;